import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { useLang } from "../context/LanguageContext";

const Navbar = () => {
  const { t, lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/chi-siamo", label: t("nav.about") },
    { to: "/interdanza", label: t("nav.interdanza") },
    { to: "/arte-e-natura", label: t("nav.arteNatura") },
    { to: "/archivio", label: t("nav.archive") },
    { to: "/novita", label: t("nav.news") },
    { to: "/gallery", label: t("nav.gallery") },
    { to: "/contatti", label: t("nav.contact") },
  ];

  const isHome = location.pathname === "/";
  const solid = scrolled || !isHome || open;

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        solid
          ? "bg-[#faf7f2]/95 backdrop-blur-md shadow-[0_1px_0_rgba(0,0,0,0.06)]"
          : "bg-transparent"
      }`}
    >
      <nav className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="flex h-[80px] lg:h-[92px] items-center justify-between">
          <Link to="/" className="flex items-center">
            <img
              src="/arts4all-logo-tight.png"
              alt="arts4all — Associazione Interculturale"
              className={`h-12 lg:h-16 w-auto transition-all duration-300 ${
                solid ? "" : "brightness-0 invert"
              }`}
            />
          </Link>

          {/* Desktop links */}
          <div className="hidden lg:flex items-center gap-7">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `relative text-[13px] uppercase tracking-[0.14em] transition-colors after:absolute after:-bottom-1.5 after:left-0 after:h-[1.5px] after:bg-[#b5533a] after:transition-all after:duration-300 ${
                    isActive ? "after:w-full" : "after:w-0 hover:after:w-full"
                  } ${
                    solid
                      ? isActive
                        ? "text-[#b5533a]"
                        : "text-[#3d3a35] hover:text-[#b5533a]"
                      : "text-white/90 hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>

          <div className="flex items-center gap-3">
            {/* Language switch */}
            <div
              className={`hidden sm:flex items-center gap-1 text-[12px] font-medium ${
                solid ? "text-[#3d3a35]" : "text-white/90"
              }`}
            >
              <Globe className="w-4 h-4 opacity-70" />
              <button
                onClick={() => setLang("it")}
                className={`px-1 transition-colors ${
                  lang === "it" ? "text-[#b5533a] font-semibold" : "hover:opacity-70"
                }`}
              >
                IT
              </button>
              <span className="opacity-40">/</span>
              <button
                onClick={() => setLang("en")}
                className={`px-1 transition-colors ${
                  lang === "en" ? "text-[#b5533a] font-semibold" : "hover:opacity-70"
                }`}
              >
                EN
              </button>
            </div>

            <button
              className={`lg:hidden p-2 -mr-2 ${solid ? "text-[#1c1b1a]" : "text-white"}`}
              onClick={() => setOpen((o) => !o)}
              aria-label="Menu"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height] duration-500 bg-[#faf7f2] ${
          open ? "max-h-[560px]" : "max-h-0"
        }`}
      >
        <div className="px-5 pb-6 pt-2 flex flex-col">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={({ isActive }) =>
                `py-3 border-b border-[#eae4d9] text-sm uppercase tracking-[0.14em] ${
                  isActive ? "text-[#b5533a]" : "text-[#3d3a35]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <div className="flex items-center gap-2 pt-4 text-sm">
            <Globe className="w-4 h-4 text-[#8a8478]" />
            <button
              onClick={() => setLang("it")}
              className={lang === "it" ? "text-[#b5533a] font-semibold" : "text-[#3d3a35]"}
            >
              Italiano
            </button>
            <span className="opacity-40">/</span>
            <button
              onClick={() => setLang("en")}
              className={lang === "en" ? "text-[#b5533a] font-semibold" : "text-[#3d3a35]"}
            >
              English
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
